import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { verificationsApi } from "@/api/verifications";
import { isValidBadgeSlug } from "@/lib/badgeSlug";

/**
 * EmbedBadgePage — /embed/:badgeSlug, Phase 4C.
 *
 * Lightweight iframe-embeddable card that customers paste onto their site:
 *
 *   <iframe src="https://xpert.company/embed/saas-nda-v2"
 *           width="360" height="180" style="border:0;" loading="lazy"
 *           title="Xpert Verified"></iframe>
 *
 * Matches the `.xv-card` style in `_design-system/widget/VerifiedBadge.html`:
 * green check + verdict ribbon, italic quote, expert byline + Xpert Verified ✓
 * stamp. The entire card is a link to /verified/:slug with target="_top" so
 * clicks escape the iframe.
 *
 * Background is intentionally transparent — the host page's bg shows through
 * (or stays white if no parent bg). `body { background: transparent }` is
 * applied via a useEffect side-effect so the rest of the app's default canvas
 * doesn't leak in.
 */
export default function EmbedBadgePage() {
  const { badgeSlug } = useParams();
  const [badge, setBadge]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  // Strip the SPA's default paper background so the iframe is transparent on
  // host pages with any color scheme. Cleanup restores the prior value if a
  // user navigates back to a non-embed route in the same tab.
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = "transparent";
    return () => { document.body.style.background = prev; };
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!isValidBadgeSlug(badgeSlug)) {
      setError(true);
      setLoading(false);
      return () => { mounted = false; };
    }
    verificationsApi.getPublicBadge(badgeSlug)
      .then((data) => { if (mounted) setBadge(data); })
      .catch(() => { if (mounted) setError(true); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [badgeSlug]);

  const href = `https://xpert.company/verified/${badgeSlug}`;
  const issued = badge?.issued_at
    ? new Date(badge.issued_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";

  if (loading) {
    return (
      <div className="font-body w-[340px] p-[18px_22px]
                      bg-white border border-slateline-200 rounded-xl
                      flex items-center justify-center min-h-[140px]">
        <div className="w-5 h-5 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Failure mode: render a minimal "Xpert Verified" stamp that still links
  // home. Better than an empty iframe — preserves brand presence even when
  // the slug is bad / hidden.
  if (error || !badge) {
    return (
      <a
        href="https://xpert.company"
        target="_top"
        rel="noopener"
        className="font-body inline-flex items-center gap-2.5 w-[340px] p-[14px_18px]
                   bg-white border border-slateline-200 rounded-xl text-ink no-underline
                   hover:border-brand-green/40 transition-colors"
      >
        <BrandMark size={28} />
        <div>
          <div className="text-[13px] font-semibold leading-tight">Xpert Verified</div>
          <div className="text-[11px] text-slateline-500 mt-0.5">The human review layer for AI-built work</div>
        </div>
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_top"
      rel="noopener"
      className="font-body inline-block w-[340px] p-[18px_22px]
                 bg-white border border-slateline-200 rounded-xl
                 text-ink no-underline
                 transition-[border-color,box-shadow] duration-150
                 hover:border-brand-green/40 hover:shadow-[0_8px_24px_-12px_rgba(14,20,16,0.08)]"
    >
      {/* Head — brand mark + verdict ribbon */}
      <div className="flex items-center gap-3">
        <BrandMark size={32} />
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold leading-tight truncate">
            {badge.title || badge.category_label || badge.category || "Reviewed"} · approved
          </div>
          {badge.summary_headline && (
            <div className="text-[11px] text-brand-green font-semibold tracking-[0.04em] mt-0.5 truncate">
              {badge.summary_headline}
            </div>
          )}
        </div>
      </div>

      {/* Body — italic quote */}
      {(badge.expert_summary || badge.summary || badge.verdict_summary) && (
        <div className="mt-[14px] font-display italic text-[15px] leading-[1.45] text-ink line-clamp-2">
          "{badge.expert_summary || badge.summary || badge.verdict_summary}"
        </div>
      )}

      {/* Foot — byline + Xpert Verified stamp */}
      <div className="mt-[14px] pt-[12px] border-t border-slateline-100
                      flex items-center justify-between text-[11px] text-slateline-500">
        <span className="truncate flex-1 mr-3">
          {badge.expert_name || "Verified expert"}
          {badge.expert_credential ? ` · ${badge.expert_credential}` : ""}
          {issued ? ` · ${issued}` : ""}
        </span>
        <span className="font-display text-[13px] font-semibold tracking-[-0.008em] text-ink whitespace-nowrap">
          Xpert Verified ✓
        </span>
      </div>
    </a>
  );
}

// Inline brand mark — green rounded square + white check. Kept inline (not
// imported from CheckELogo) so the embed doesn't drag in unrelated app code.
function BrandMark({ size = 32 }) {
  const r  = Math.round(size * 0.25);
  const sw = Math.max(2.5, size / 10);
  return (
    <span
      aria-hidden="true"
      className="inline-grid place-items-center flex-shrink-0"
      style={{ width: size, height: size, background: "#17803D", borderRadius: r }}
    >
      <svg width={size * 0.62} height={size * 0.62} viewBox="0 0 24 24" fill="none">
        <path d="M5 12.5 L10 17.5 L19 7"
              stroke="#fff" strokeWidth={sw / (size * 0.62 / 24)}
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
